import type { Surface } from '@funkit/connect-core'
import type { ApplePayEnvironment } from '@transak/ui-components-core'

import { logger } from '../../logging/logger'
import type { SurfaceAdapter, SurfaceContext } from './adapter'
import { HostedControlFrame } from './HostedControlFrame'
import { TransakApplePaySurface } from './TransakApplePaySurface'

const TRANSAK_ENVIRONMENTS = new Set<string>([
  'LOCAL',
  'DEVELOPMENT',
  'STAGING',
  'PRODUCTION',
])

interface TransakParams {
  sessionId: string
  environment: ApplePayEnvironment
}

/**
 * The session this surface names, or `undefined` when it is not a Transak
 * Apple Pay sheet — or is one the adapter cannot vouch for.
 */
function readTransakApplePayParams(
  surface: Surface,
): TransakParams | undefined {
  if (surface.kind !== 'PAY_SHEET' || surface.provider !== 'TRANSAK') {
    return undefined
  }
  // The contract requires `params`, but no response is validated before it
  // reaches here, so an absent one must fail the match rather than throw.
  const params: unknown = surface.params
  if (params === null || typeof params !== 'object') {
    return undefined
  }
  const { environment, sessionId } = params as Record<string, unknown>
  if (
    typeof sessionId !== 'string' ||
    sessionId.length === 0 ||
    typeof environment !== 'string' ||
    !TRANSAK_ENVIRONMENTS.has(environment)
  ) {
    return undefined
  }
  return { sessionId, environment: environment as ApplePayEnvironment }
}

/** Transak draws the Apple Pay button itself, inside a WebView. */
export const transakApplePay: SurfaceAdapter = (surface) => {
  const params = readTransakApplePayParams(surface)
  if (params === undefined) {
    return undefined
  }

  return { control: 'EMBEDDED', render: renderer(params) }
}

const renderer =
  (params: TransakParams) =>
  ({ placement, onReady, onFailed, report }: SurfaceContext) => (
    // Black with a hairline edge: Transak's button is black on black, so the
    // ring is what gives it an outline against the surface behind it.
    <HostedControlFrame
      fill="#000000"
      edgeColor="#FFFFFF"
      placement={placement}
    >
      <TransakApplePaySurface
        sessionId={params.sessionId}
        environment={params.environment}
        onReady={onReady}
        onSuccess={({ requestId }) => report({ requestId })}
        onError={({ code }) => {
          logger.warn('connect-rn:fiat:transak_apple_pay_failed', { code })
          // Uncovering here would leave a button the errored machine can no
          // longer act on, so the screen owns the failure instead.
          onFailed(code)
        }}
      />
    </HostedControlFrame>
  )
